import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", internal_id: "", external_machine_id: "", provider: "mock", location_id: "" };

export default function Automaten() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const machines = useQuery({
    queryKey: ["vending-machines"],
    queryFn: async () => (await api.get("/vending/machines")).data,
  });
  const locations = useQuery({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/locations", { params: { type: "vending_machine" } })).data,
  });
  const providers = useQuery({
    queryKey: ["vending-providers"],
    queryFn: async () => (await api.get("/vending/providers")).data.providers,
  });
  const create = useMutation({
    mutationFn: async () => (await api.post("/vending/machines", form)).data,
    onSuccess: () => {
      toast.success("Automat angelegt");
      setOpen(false); setForm(EMPTY);
      qc.invalidateQueries({ queryKey: ["vending-machines"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });
  const pull = useMutation({
    mutationFn: async (provider) => (await api.post(`/vending/ingest/pull?provider=${provider}`)).data,
    onSuccess: (data) => {
      const processed = data.filter((r) => r.status === "PROCESSED").length;
      toast.success(`${processed} Events verarbeitet`);
      qc.invalidateQueries({ queryKey: ["vending-machines"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const locName = (id) => locations.data?.find((l) => l.id === id)?.name || "—";

  return (
    <div data-testid="vending-machines-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-cyan-400">Integrationen</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight truncate">Automaten</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="pull-mock-btn"
            variant="outline"
            className="border-zinc-700 rounded-sm uppercase tracking-wider"
            onClick={() => pull.mutate("mock")}
          >
            <RefreshCcw size={14} className="mr-2" /> Mock-Sync ausführen
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-machine-btn"
                className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-sm uppercase tracking-wider font-semibold"
              >
                <Plus size={16} className="mr-2" /> Neuer Automat
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-950 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="font-display uppercase">Neuer Automat</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {[
                  ["name", "Name *"],
                  ["internal_id", "Interne ID *"],
                  ["external_machine_id", "External Machine ID"],
                ].map(([k, l]) => (
                  <div key={k}>
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">{l}</Label>
                    <Input
                      data-testid={`vm-field-${k}`}
                      value={form[k] || ""}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm"
                    />
                  </div>
                ))}
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">Provider</Label>
                  <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                    <SelectTrigger data-testid="vm-field-provider" className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      {(providers.data || []).map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">Lagerort *</Label>
                  <Select value={form.location_id} onValueChange={(v) => setForm({ ...form, location_id: v })}>
                    <SelectTrigger data-testid="vm-field-location" className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm">
                      <SelectValue placeholder="Auswählen…" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      {(locations.data || []).map((l) => (
                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Abbrechen</Button>
                <Button
                  data-testid="vm-save-btn"
                  onClick={() => create.mutate()}
                  disabled={!form.name || !form.internal_id || !form.location_id}
                  className="bg-cyan-400 text-zinc-950 hover:bg-cyan-300 rounded-sm"
                >
                  Speichern
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="border border-zinc-800 bg-zinc-900 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3 font-mono">Interne ID</th>
              <th className="text-left px-4 py-3">Provider</th>
              <th className="text-left px-4 py-3 font-mono">External ID</th>
              <th className="text-left px-4 py-3">Lager</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3 font-mono">Letzter Sync</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800" data-testid="vending-machines-tbody">
            {(machines.data || []).map((m) => (
              <tr key={m.id} className="hover:bg-zinc-800/60">
                <td className="px-4 py-3">{m.name}</td>
                <td className="px-4 py-3 font-mono">{m.internal_id}</td>
                <td className="px-4 py-3 text-cyan-400 uppercase">{m.provider}</td>
                <td className="px-4 py-3 font-mono text-zinc-400">{m.external_machine_id || "—"}</td>
                <td className="px-4 py-3">{locName(m.location_id)}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs uppercase tracking-wider ${m.active ? "text-emerald-400" : "text-zinc-500"}`}>
                    {m.active ? "Aktiv" : "Inaktiv"}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-400">
                  {m.last_sync_at ? new Date(m.last_sync_at).toLocaleString("de-DE") : "—"}
                </td>
              </tr>
            ))}
            {!machines.data?.length && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">Noch keine Automaten.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
